import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Shield,
  Network,
  Box,
  Layers,
  Landmark,
  User,
  ShieldCheck,
  BarChart3,
  ArrowRight,
  ArrowUpRight,
  Coins,
  Play,
  Mic,
} from 'lucide-react';
import { colors } from '../styles/colors';
import { fetchAZGReserveData, fetchAZSReserveData } from '../services/airtableService';
import { formatCurrency, formatCurrencyFixed } from '../utils/formatters';
import { BTX_REFERRAL_URL } from '../config/links';

const BTX_SIGNUP_URL = BTX_REFERRAL_URL;

// ---------- Reusable bits ----------
const SectionHeading = ({ eyebrow, title, sub, align = 'center' }) => (
  <div className={`max-w-3xl ${align === 'center' ? 'mx-auto text-center' : ''} mb-12 sm:mb-16`}>
    {eyebrow && (
      <div
        className={`flex items-center gap-3 mb-4 ${align === 'center' ? 'justify-center' : ''}`}
      >
        <span
          className="h-px w-8"
          style={{ backgroundColor: colors.teal }}
        />
        <span
          className="text-xs uppercase tracking-[0.22em] font-medium"
          style={{ color: colors.teal }}
        >
          {eyebrow}
        </span>
      </div>
    )}
    <h2
      className="text-3xl sm:text-4xl md:text-5xl font-bold mb-4 tracking-tight"
      style={{ color: colors.white }}
    >
      {title}
    </h2>
    {sub && (
      <p
        className="text-base sm:text-lg leading-relaxed"
        style={{ color: colors.white, opacity: 0.65 }}
      >
        {sub}
      </p>
    )}
  </div>
);

// ---------- Page ----------
const AZGLandingPage = () => {
  const [isVisible, setIsVisible] = useState(false);
  const [azgData, setAzgData] = useState(null);
  const [azsData, setAzsData] = useState(null);
  const [reserveLoading, setReserveLoading] = useState(true);
  const [anchorVisible, setAnchorVisible] = useState(false);
  const [activeAnchor, setActiveAnchor] = useState('overview');
  const navigate = useNavigate();

  const anchors = [
    { id: 'overview', label: 'Overview' },
    { id: 'azreserves', label: 'AZReserves' },
    { id: 'marketplace', label: 'Marketplace' },
    { id: 'why-azg', label: 'Why AZG' },
  ];

  const handleAnchorClick = (e, id) => {
    e.preventDefault();
    const el = document.getElementById(id);
    if (!el) return;
    // Account for fixed navbar (~96px desktop / 72px mobile) + anchor bar (~44px)
    const offset = window.innerWidth >= 640 ? 140 : 116;
    const top = el.getBoundingClientRect().top + window.pageYOffset - offset;
    window.scrollTo({ top, behavior: 'smooth' });
  };

  useEffect(() => {
    const raf = requestAnimationFrame(() => setIsVisible(true));
    window.scrollTo(0, 0);
    return () => cancelAnimationFrame(raf);
  }, []);

  // Load AZG + AZS reserve data using existing service (single source of truth)
  useEffect(() => {
    let cancelled = false;
    const loadReserve = async () => {
      try {
        const [g, s] = await Promise.all([
          fetchAZGReserveData(),
          fetchAZSReserveData(),
        ]);
        if (!cancelled) {
          setAzgData(g);
          setAzsData(s);
        }
      } catch (_e) {
        // External API CORS / failure — render '—' placeholders
        if (!cancelled) {
          setAzgData(null);
          setAzsData(null);
        }
      } finally {
        if (!cancelled) setReserveLoading(false);
      }
    };
    loadReserve();
    return () => {
      cancelled = true;
    };
  }, []);

  // Anchor bar visibility — appears after slight scroll past hero
  useEffect(() => {
    const onScroll = () => {
      // Reveal once user scrolls ~40% of viewport height
      setAnchorVisible(window.scrollY > window.innerHeight * 0.4);
    };
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  // Active section highlighting via IntersectionObserver
  useEffect(() => {
    const ids = ['overview', 'azreserves', 'marketplace', 'why-azg'];
    const elements = ids
      .map((id) => document.getElementById(id))
      .filter(Boolean);
    if (elements.length === 0) return undefined;

    const observer = new IntersectionObserver(
      (entries) => {
        // Pick the entry most prominently in view
        const visible = entries
          .filter((e) => e.isIntersecting)
          .sort((a, b) => b.intersectionRatio - a.intersectionRatio);
        if (visible[0]) {
          setActiveAnchor(visible[0].target.id);
        }
      },
      {
        // Trigger when section is roughly center-of-viewport
        rootMargin: '-35% 0px -55% 0px',
        threshold: [0, 0.25, 0.5, 0.75, 1],
      }
    );
    elements.forEach((el) => observer.observe(el));
    return () => observer.disconnect();
  }, []);

  // Mirror existing live reserve values already shown elsewhere on the site
  // (same service used by LiveReserveData / Reserve Data pages — single source of truth)
  const azgTokenValue = azgData?.currentTokenValue ?? null;
  const azsTokenValue = azsData?.currentTokenValue ?? null;
  const combinedReserveValue =
    azgData?.totalReserveValue != null || azsData?.totalReserveValue != null
      ? (azgData?.totalReserveValue || 0) + (azsData?.totalReserveValue || 0)
      : null;

  const heroSnapshotItems = [
    {
      label: 'AZG Value',
      value: azgTokenValue,
      format: 'currency',
      testid: 'hero-snapshot-azg-value',
    },
    {
      label: 'AZS Value',
      value: azsTokenValue,
      format: 'currency',
      testid: 'hero-snapshot-azs-value',
    },
    {
      label: 'Reserve Value',
      value: combinedReserveValue,
      format: 'currencyFixed',
      testid: 'hero-snapshot-reserve-value',
    },
    {
      label: 'Asset Classes',
      value: 'Gold / Silver',
      format: 'text',
      testid: 'hero-snapshot-asset-classes',
    },
  ];

  // Comparison rows — Traditional vs AZG framework (no named competitors, no redeemability claims)
  const comparisonRows = [
    {
      icon: Box,
      feature: 'Reserve Structure',
      sub: 'How the framework is structured',
      traditional: 'Often linked primarily to vaulted reserve assets',
      azg: 'Linked to pledged bullion, in-ground and above-ground reserve assets',
    },
    {
      icon: BarChart3,
      feature: 'Reserve Dynamics',
      sub: 'How the reserve evolves over time',
      traditional: 'Often structured around static reserve positioning',
      azg: 'The reserve asset base can expand through verified asset delivery',
    },
    {
      icon: Network,
      feature: 'Primary Function',
      sub: 'What the framework is built for',
      traditional: 'Often focused on reserve representation and marketplace liquidity',
      azg: 'Structured to support reserve-backed collateral and forward contract environments',
    },
    {
      icon: Landmark,
      feature: 'Marketplace Context',
      sub: 'How the framework reaches participants',
      traditional: 'Often built for broad exchange availability and transactional use',
      azg: 'Designed around reserve growth where verified asset delivery strengthens the broader reserve framework over time',
    },
  ];

  return (
    <div className="min-h-screen" style={{ backgroundColor: '#050505' }}>
      {/* ==========  IN-PAGE ANCHOR NAV  ========== */}
      <div
        data-testid="azg-anchor-nav"
        aria-hidden={!anchorVisible}
        className={`fixed left-0 right-0 z-40 transition-all duration-500 ease-out ${
          anchorVisible
            ? 'opacity-100 translate-y-0 pointer-events-auto'
            : 'opacity-0 -translate-y-2 pointer-events-none'
        }`}
        style={{
          top: 'var(--azg-anchor-top, 72px)',
        }}
      >
        <style>{`
          :root { --azg-anchor-top: 72px; }
          @media (min-width: 640px) { :root { --azg-anchor-top: 96px; } }
          .azg-anchor-scroll::-webkit-scrollbar { display: none; }
          .azg-anchor-scroll { -ms-overflow-style: none; scrollbar-width: none; }
        `}</style>
        <div
          className="border-y"
          style={{
            background: 'rgba(5,5,5,0.72)',
            borderColor: 'rgba(255,255,255,0.06)',
            backdropFilter: 'blur(18px) saturate(140%)',
            WebkitBackdropFilter: 'blur(18px) saturate(140%)',
            boxShadow: '0 1px 0 rgba(0,128,128,0.04), 0 8px 24px -16px rgba(0,0,0,0.5)',
          }}
        >
          <nav
            aria-label="Page sections"
            className="azg-anchor-scroll max-w-7xl mx-auto px-4 sm:px-6 lg:px-10 overflow-x-auto"
          >
            <ul className="flex items-center gap-5 sm:gap-8 h-11 sm:h-12 whitespace-nowrap">
              {anchors.map((a) => {
                const isActive = activeAnchor === a.id;
                return (
                  <li key={a.id} className="flex-shrink-0">
                    <a
                      href={`#${a.id}`}
                      onClick={(e) => handleAnchorClick(e, a.id)}
                      data-testid={`azg-anchor-${a.id}`}
                      aria-current={isActive ? 'true' : undefined}
                      className="relative inline-flex items-center text-[10.5px] sm:text-[11px] uppercase tracking-[0.22em] font-medium transition-colors duration-300 py-2"
                      style={{
                        color: isActive ? colors.teal : 'rgba(255,255,255,0.55)',
                        fontFamily: "'Montserrat', system-ui, sans-serif",
                      }}
                      onMouseEnter={(e) => {
                        if (!isActive) e.currentTarget.style.color = 'rgba(255,255,255,0.9)';
                      }}
                      onMouseLeave={(e) => {
                        if (!isActive) e.currentTarget.style.color = 'rgba(255,255,255,0.55)';
                      }}
                    >
                      {a.label}
                      <span
                        aria-hidden="true"
                        className="absolute left-0 right-0 -bottom-[1px] h-px transition-all duration-300"
                        style={{
                          backgroundColor: colors.teal,
                          opacity: isActive ? 1 : 0,
                          boxShadow: isActive ? `0 0 8px ${colors.teal}80` : 'none',
                        }}
                      />
                    </a>
                  </li>
                );
              })}
            </ul>
          </nav>
        </div>
      </div>

      {/* ==========  HERO — Cinematic full-bleed BTX  ========== */}
      <section
        id="overview"
        className="relative overflow-hidden"
        style={{
          minHeight: '85vh',
          background:
            'linear-gradient(180deg, #000000 0%, #0B0F14 60%, #111827 100%)',
        }}
      >
        {/* Full-bleed cinematic image */}
        <picture>
          <source srcSet="/btx-hero.webp" type="image/webp" />
          <img
            src="/btx-hero.jpg"
            alt=""
            aria-hidden="true"
            data-testid="btx-hero-image"
            className={`absolute inset-0 w-full h-full object-cover btx-hero-drift transition-opacity duration-[2000ms] ease-out ${
              isVisible ? 'opacity-100' : 'opacity-0'
            }`}
            style={{
              objectPosition: 'center right',
            }}
            loading="eager"
            decoding="async"
          />
        </picture>

        {/* Left-side dark gradient for text legibility */}
        <div
          aria-hidden="true"
          className="absolute inset-0 pointer-events-none"
          style={{
            background:
              'linear-gradient(90deg, #000000 0%, rgba(0,0,0,0.85) 25%, rgba(11,15,20,0.55) 50%, rgba(11,15,20,0.15) 75%, rgba(11,15,20,0) 100%)',
          }}
        />

        {/* Subtle ambient teal lift on bottom-left */}
        <div
          aria-hidden="true"
          className="absolute inset-0 pointer-events-none"
          style={{
            background:
              'radial-gradient(60% 50% at 15% 75%, rgba(0,128,128,0.10) 0%, rgba(0,0,0,0) 70%)',
          }}
        />

        {/* Top + bottom vignette for cinematic framing */}
        <div
          aria-hidden="true"
          className="absolute inset-0 pointer-events-none"
          style={{
            background:
              'linear-gradient(180deg, rgba(0,0,0,0.4) 0%, rgba(0,0,0,0) 18%, rgba(0,0,0,0) 80%, rgba(0,0,0,0.55) 100%)',
          }}
        />

        {/* Content */}
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-10 flex items-center min-h-[85vh] pt-28 pb-20 sm:pt-32 sm:pb-24">
          <div
            className={`max-w-2xl btx-hero-text transition-all duration-1000 ease-out transform ${
              isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'
            }`}
            style={{ fontFamily: "'Montserrat', system-ui, sans-serif" }}
          >
            {/* Eyebrow */}
            <div className="flex items-center gap-3 mb-7">
              <span
                className="h-px w-10"
                style={{ backgroundColor: colors.teal, opacity: 0.7 }}
              />
              <span
                className="text-[11px] uppercase tracking-[0.32em] font-medium"
                style={{ color: colors.teal, fontFamily: 'inherit' }}
              >
                BTX Secondary Marketplace
              </span>
            </div>

            <h1
              className="text-4xl sm:text-5xl lg:text-6xl xl:text-[68px] leading-[1.04] tracking-[-0.02em] mb-7"
              style={{
                color: colors.white,
                fontFamily: 'inherit',
                fontWeight: 700,
              }}
            >
              ACCESS GOLD-BACKED DIGITAL ASSETS THROUGH THE BTX MARKETPLACE.
            </h1>

            <p
              className="text-base sm:text-lg lg:text-xl leading-[1.55] max-w-xl mb-12"
              style={{
                color: 'rgba(255,255,255,0.72)',
                fontFamily: 'inherit',
                fontWeight: 400,
              }}
            >
              The BTX secondary marketplace provides structured and fractional access to gold-backed digital assets, including AZG and AZS, through a compliant onboarding and verification framework.
            </p>

            <div className="flex flex-col sm:flex-row gap-3 sm:gap-4">
              <a
                href={BTX_SIGNUP_URL}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="btx-hero-create-btx-btn"
                className="inline-flex items-center justify-center gap-2 px-8 py-4 rounded-lg text-sm font-semibold uppercase tracking-[0.16em] transition-all duration-300"
                style={{
                  backgroundColor: colors.teal,
                  color: colors.white,
                  border: `1px solid ${colors.teal}`,
                  fontFamily: 'inherit',
                  textDecoration: 'none',
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.boxShadow = `0 0 0 4px ${colors.teal}26`;
                  e.currentTarget.style.transform = 'translateY(-1px)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.boxShadow = 'none';
                  e.currentTarget.style.transform = 'translateY(0)';
                }}
              >
                Continue To BTX Marketplace
                <ArrowUpRight className="w-4 h-4" />
              </a>
              <button
                onClick={() => {
                  const scrollToSnapshot = (attempts = 0) => {
                    const el = document.getElementById('live-reserve-snapshot');
                    if (el) {
                      el.scrollIntoView({ behavior: 'smooth', block: 'start' });
                    } else if (attempts < 20) {
                      setTimeout(() => scrollToSnapshot(attempts + 1), 100);
                    }
                  };
                  if (window.location.pathname === '/') {
                    scrollToSnapshot();
                  } else {
                    navigate('/');
                    setTimeout(() => scrollToSnapshot(), 200);
                  }
                }}
                data-testid="btx-hero-reserve-data-btn"
                className="inline-flex items-center justify-center gap-2 px-8 py-4 rounded-lg text-sm font-semibold uppercase tracking-[0.16em] transition-all duration-300"
                style={{
                  backgroundColor: 'transparent',
                  color: colors.white,
                  border: `1px solid rgba(255,255,255,0.25)`,
                  fontFamily: 'inherit',
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.backgroundColor = 'rgba(255,255,255,0.06)';
                  e.currentTarget.style.borderColor = 'rgba(255,255,255,0.45)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.backgroundColor = 'transparent';
                  e.currentTarget.style.borderColor = 'rgba(255,255,255,0.25)';
                }}
              >
                Reserve Data
                <ArrowRight className="w-4 h-4" />
              </button>
            </div>

            {/* Feature icons row */}
            <div className="grid grid-cols-3 gap-4 sm:gap-6 max-w-xl mt-12">
              {[
                { Icon: Shield, label: 'Verified\nParticipation' },
                { Icon: Layers, label: 'Mineral-Linked\nAssets' },
                { Icon: Landmark, label: 'Structured\nMarketplace Access' },
              ].map(({ Icon, label }) => (
                <div key={label} className="flex items-center gap-2.5">
                  <Icon
                    className="w-5 h-5 flex-shrink-0"
                    style={{ color: colors.teal }}
                    strokeWidth={1.5}
                  />
                  <span
                    className="text-xs sm:text-sm whitespace-pre-line leading-tight"
                    style={{
                      color: colors.white,
                      opacity: 0.7,
                      fontFamily: 'inherit',
                    }}
                  >
                    {label}
                  </span>
                </div>
              ))}
            </div>

            {/* ===== Slim Reserve Snapshot Strip (mirrors existing live reserve data) ===== */}
            <div
              data-testid="hero-reserve-snapshot-strip"
              className="mt-8 sm:mt-10 max-w-2xl rounded-xl overflow-hidden"
              style={{
                background:
                  'linear-gradient(180deg, rgba(255,255,255,0.05) 0%, rgba(255,255,255,0.02) 100%)',
                border: '1px solid rgba(255,255,255,0.10)',
                boxShadow:
                  'inset 0 1px 0 rgba(255,255,255,0.05), 0 10px 30px -15px rgba(0,0,0,0.6), 0 0 0 1px rgba(0,128,128,0.04)',
                backdropFilter: 'blur(14px)',
                WebkitBackdropFilter: 'blur(14px)',
                fontFamily: 'inherit',
              }}
            >
              <div className="flex flex-wrap sm:flex-nowrap divide-x divide-white/10">
                {heroSnapshotItems.map((item) => {
                  let display;
                  if (item.format === 'text') {
                    display = item.value;
                  } else if (reserveLoading) {
                    display = '—';
                  } else if (item.value === null) {
                    display = '—';
                  } else if (item.format === 'currencyFixed') {
                    display = formatCurrencyFixed(item.value);
                  } else {
                    display = formatCurrency(item.value);
                  }
                  return (
                    <div
                      key={item.label}
                      data-testid={item.testid}
                      className="flex-1 min-w-[50%] sm:min-w-0 px-3.5 sm:px-4 py-3 sm:py-3.5"
                    >
                      <span
                        className="block text-[9.5px] sm:text-[10px] uppercase tracking-[0.2em] font-medium mb-1"
                        style={{
                          color: colors.white,
                          opacity: 0.5,
                          fontFamily: 'inherit',
                        }}
                      >
                        {item.label}
                      </span>
                      <span
                        className="block text-[13px] sm:text-sm font-semibold leading-snug tracking-tight"
                        style={{
                          color: colors.white,
                          fontVariantNumeric: 'tabular-nums',
                          fontFamily: 'inherit',
                        }}
                      >
                        {display}
                        {item.format !== 'text' && display !== '—' && (
                          <span
                            className="ml-1 text-[10px] sm:text-[11px] font-medium"
                            style={{
                              color: colors.white,
                              opacity: 0.45,
                              fontFamily: 'inherit',
                            }}
                          >
                            USD
                          </span>
                        )}
                      </span>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        </div>

        <style>{`
          @keyframes btx-hero-drift {
            0%   { transform: scale(1.03) translate3d(0, 0, 0); }
            50%  { transform: scale(1.05) translate3d(-0.6%, -0.6%, 0); }
            100% { transform: scale(1.03) translate3d(0, 0, 0); }
          }
          .btx-hero-drift { animation: btx-hero-drift 22s ease-in-out infinite; will-change: transform; }
          @media (prefers-reduced-motion: reduce) {
            .btx-hero-drift { animation: none; }
          }
        `}</style>
      </section>

      {/* ==========  UNDERSTANDING AZRESERVES  ========== */}
      <section id="azreserves" className="py-20 sm:py-28" style={{ backgroundColor: '#040404' }}>
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div
            className="rounded-3xl px-6 sm:px-10 lg:px-16 py-10 sm:py-12 lg:py-14"
            style={{
              background:
                'linear-gradient(180deg, rgba(255,255,255,0.025) 0%, rgba(255,255,255,0.01) 100%)',
              border: '1px solid rgba(255,255,255,0.06)',
              boxShadow:
                '0 30px 60px -30px rgba(0,0,0,0.6), inset 0 1px 0 rgba(255,255,255,0.04)',
            }}
          >
            <SectionHeading
              title="UNDERSTANDING AZRESERVES"
              sub="A clearer introduction to the reserve-backed digital asset framework behind AZG and AZS."
            />

            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-7">
              {[
                {
                  Icon: Coins,
                  title: 'What is AZG?',
                  body: 'AZG is a reserve-backed digital asset linked to pledged bullion, in-ground and above ground resources.',
                },
                {
                  Icon: Network,
                  title: 'Why does it exist?',
                  body: 'AZG was created to bring reserve-backed assets into modern digital environments with greater transparency, structure, and accessibility.',
                },
                {
                  Icon: Landmark,
                  title: "Who's AZG for?",
                  body: 'AZG is designed for participants engaging with reserve-backed digital assets within a structured long-term reserve environment.',
                },
              ].map(({ Icon, title, body }) => (
                <div
                  key={title}
                  data-testid={`understanding-card-${title.toLowerCase().replace(/[^a-z]+/g, '-').replace(/^-|-$/g, '')}`}
                  className="flex flex-col items-center text-center p-8 lg:p-10 rounded-2xl h-full transition-all duration-500 hover:-translate-y-1"
                  style={{
                    backgroundColor: 'rgba(255,255,255,0.025)',
                    border: '1px solid rgba(255,255,255,0.06)',
                  }}
                >
                  <div
                    className="w-12 h-12 rounded-xl flex items-center justify-center mb-8"
                    style={{
                      backgroundColor: 'rgba(255,255,255,0.04)',
                      border: '1px solid rgba(255,255,255,0.08)',
                    }}
                  >
                    <Icon
                      className="w-6 h-6"
                      style={{ color: colors.teal }}
                      strokeWidth={1.5}
                    />
                  </div>

                  <h3
                    className="text-xl sm:text-2xl font-semibold mb-4 leading-tight tracking-tight"
                    style={{ color: colors.white }}
                  >
                    {title}
                  </h3>

                  <p
                    className="text-base leading-[1.65] max-w-xs"
                    style={{ color: colors.white, opacity: 0.65 }}
                  >
                    {body}
                  </p>
                </div>
              ))}
            </div>

            {/* ===== The Reserve Grows Stronger — integrated emphasis block ===== */}
            <div
              className="relative overflow-hidden rounded-2xl p-8 sm:p-10 lg:p-12 mt-10 sm:mt-12"
              style={{
                backgroundColor: 'rgba(242,214,117,0.04)',
                border: `1px solid ${colors.goldMid}33`,
              }}
              data-testid="reserve-grows-stronger-block"
            >
              {/* Gold glow accent */}
              <div
                aria-hidden="true"
                className="absolute -top-20 -right-20 w-64 h-64 rounded-full pointer-events-none"
                style={{
                  background:
                    'radial-gradient(circle, rgba(242,214,117,0.18) 0%, rgba(242,214,117,0) 70%)',
                  filter: 'blur(20px)',
                }}
              />

              <div className="relative text-center max-w-3xl mx-auto">
                <h3
                  className="text-xl sm:text-2xl font-bold uppercase tracking-[0.14em] mb-3"
                  style={{ color: colors.goldMid }}
                >
                  The Reserve Grows Stronger
                </h3>
                <p
                  className="text-base sm:text-lg leading-relaxed"
                  style={{ color: colors.white, opacity: 0.8 }}
                >
                  Every verified delivery into the reserve strengthens the asset base supporting the AZG framework across the reserve over time.
                </p>
              </div>
            </div>

            {/* Inline utility links */}
            <div className="mt-8 flex flex-col sm:flex-row gap-3 sm:gap-6 justify-center text-sm">
              <button
                onClick={() => navigate('/reserve-data/azg')}
                data-testid="azg-view-live-data-link"
                className="inline-flex items-center justify-center gap-2 transition-opacity hover:opacity-100"
                style={{ color: colors.teal, opacity: 0.85, background: 'transparent', border: 'none', cursor: 'pointer' }}
              >
                View Live AZG Reserve Data
                <ArrowRight className="w-4 h-4" />
              </button>
              <button
                onClick={() => navigate('/reserve-architecture')}
                data-testid="azg-reserve-architecture-link"
                className="inline-flex items-center justify-center gap-2 transition-opacity hover:opacity-100"
                style={{ color: colors.white, opacity: 0.5, background: 'transparent', border: 'none', cursor: 'pointer' }}
              >
                Understand the Reserve Architecture
                <ArrowRight className="w-4 h-4" />
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* ==========  ACCESS THROUGH BTX  ========== */}
      <section id="marketplace" className="py-20 sm:py-28" style={{ backgroundColor: '#040404' }}>
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div
            className="rounded-3xl px-6 sm:px-10 lg:px-16 py-10 sm:py-12 lg:py-14"
            style={{
              background:
                'linear-gradient(180deg, rgba(255,255,255,0.025) 0%, rgba(255,255,255,0.01) 100%)',
              border: '1px solid rgba(255,255,255,0.06)',
              boxShadow:
                '0 30px 60px -30px rgba(0,0,0,0.6), inset 0 1px 0 rgba(255,255,255,0.04)',
            }}
          >
            <SectionHeading
              eyebrow="Marketplace Access"
              title="BTX SECONDARY MARKETPLACE"
              sub="BTX provides marketplace access to reserve-backed digital assets including AZG and AZS through a standard onboarding process."
            />

          <div className="relative">
            {/* Connector line (desktop only) */}
            <div
              aria-hidden="true"
              className="hidden md:block absolute top-1/2 left-[16.67%] right-[16.67%] h-px"
              style={{ backgroundColor: `${colors.teal}30`, transform: 'translateY(-50%)' }}
            />

            <div className="grid md:grid-cols-3 gap-6 md:gap-8 relative">
              {[
                {
                  num: 1,
                  Icon: User,
                  title: 'Create Account',
                  text: 'Sign up on BTX Hub to get started.',
                  href: BTX_SIGNUP_URL,
                },
                {
                  num: 2,
                  Icon: ShieldCheck,
                  title: 'Complete Verification',
                  text: 'Complete KYC and identity verification securely.',
                },
                {
                  num: 3,
                  Icon: BarChart3,
                  title: 'Explore AZG',
                  text: 'Access AZG within the BTX environment.',
                },
              ].map(({ num, Icon, title, text, href }, idx) => {
                const cardProps = href
                  ? {
                      href,
                      target: '_blank',
                      rel: 'noopener noreferrer',
                      'data-testid': 'marketplace-access-create-btx-account-btn',
                    }
                  : {};
                const CardEl = href ? 'a' : 'div';
                return (
                  <CardEl
                    key={num}
                    {...cardProps}
                    className={`relative p-6 sm:p-7 rounded-2xl text-center ${
                      href ? 'block transition-all duration-300 hover:-translate-y-0.5' : ''
                    }`}
                    style={{
                      backgroundColor: 'rgba(255,255,255,0.025)',
                      border: `1px solid ${colors.teal}24`,
                      textDecoration: 'none',
                    }}
                  >
                    {/* Step number */}
                    <div
                      className="absolute -top-4 left-1/2 -translate-x-1/2 w-9 h-9 rounded-full flex items-center justify-center text-sm font-semibold"
                      style={{
                        backgroundColor: '#070707',
                        color: colors.teal,
                        border: `1px solid ${colors.teal}80`,
                      }}
                    >
                      {num}
                    </div>

                    <div
                      className="mx-auto w-14 h-14 rounded-xl flex items-center justify-center mt-3 mb-5"
                      style={{
                        backgroundColor: `${colors.teal}14`,
                        border: `1px solid ${colors.teal}40`,
                      }}
                    >
                      <Icon className="w-7 h-7" style={{ color: colors.teal }} strokeWidth={1.5} />
                    </div>

                    <h3
                      className="text-base font-semibold uppercase tracking-[0.12em] mb-2"
                      style={{ color: colors.white }}
                    >
                      {title}
                    </h3>
                    <p
                      className="text-sm leading-relaxed"
                      style={{ color: colors.white, opacity: 0.65 }}
                    >
                      {text}
                    </p>

                    {/* Arrow between steps (desktop) */}
                    {idx < 2 && (
                      <ArrowRight
                        aria-hidden="true"
                        className="hidden md:block absolute top-1/2 -right-5 lg:-right-6 w-5 h-5 -translate-y-1/2"
                        style={{ color: colors.teal }}
                        strokeWidth={1.5}
                      />
                    )}
                  </CardEl>
                );
              })}
            </div>
          </div>

          {/* ===== Marketplace Flow — Embedded BTX Explainer Video ===== */}
          <div className="mt-16 sm:mt-20" data-testid="btx-marketplace-flow-block">
            {/* Eyebrow + Headline */}
            <div className="max-w-2xl mx-auto text-center mb-7 sm:mb-8">
              <div className="flex items-center gap-3 mb-4 justify-center">
                <span className="h-px w-8" style={{ backgroundColor: colors.teal }} />
                <span
                  className="text-[11px] uppercase tracking-[0.28em] font-medium"
                  style={{ color: colors.teal }}
                >
                  Marketplace Flow
                </span>
                <span className="h-px w-8" style={{ backgroundColor: colors.teal }} />
              </div>
              <h3
                className="text-2xl sm:text-3xl font-bold mb-4 tracking-tight leading-tight"
                style={{ color: colors.white }}
              >
                HOW MARKETPLACE SETTLEMENT WORKS
              </h3>
              <p
                className="text-sm sm:text-base leading-relaxed"
                style={{ color: colors.white, opacity: 0.65 }}
              >
                A simplified walkthrough of onboarding, order interaction, settlement, portfolio updates, and reserve-linked digital asset activity within the BTX marketplace.
              </p>
            </div>

            {/* Video Embed */}
            <div
              className="relative rounded-2xl overflow-hidden"
              style={{
                background: 'rgba(0,0,0,0.5)',
                border: '1px solid rgba(255,255,255,0.08)',
                boxShadow:
                  '0 30px 60px -30px rgba(0,0,0,0.7), inset 0 1px 0 rgba(255,255,255,0.04), 0 0 0 1px rgba(0,128,128,0.04)',
              }}
            >
              <div className="relative w-full" style={{ paddingBottom: '56.25%' }}>
                <iframe
                  className="absolute inset-0 w-full h-full"
                  src="https://www.youtube-nocookie.com/embed/mrzJL2f4Tgo?rel=0&modestbranding=1&color=white"
                  title="BTX Marketplace — How Marketplace Settlement Works"
                  data-testid="btx-marketplace-flow-video"
                  loading="lazy"
                  allow="accelerometer; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                  allowFullScreen
                  style={{ border: 0 }}
                />
              </div>
            </div>
          </div>

          {/* BTX Markets logo — subtle institutional brand attribution */}
          <div className="mt-14 flex flex-col items-center">
            <span
              className="text-[10px] uppercase tracking-[0.28em] mb-4"
              style={{ color: colors.white, opacity: 0.35 }}
            >
              Marketplace Operated By
            </span>
            <img
              src="/btx-markets-logo.png"
              alt="Black Tie BTX Markets"
              data-testid="btx-markets-logo"
              className="h-9 sm:h-10 w-auto"
              style={{
                filter: 'grayscale(1) brightness(1.4) contrast(0.85)',
                mixBlendMode: 'lighten',
                opacity: 0.45,
              }}
            />
          </div>

          {/* Operational separation disclaimer — positioned above the final CTA */}
          <div className="mt-10 pt-6 max-w-3xl mx-auto" style={{ borderTop: '1px solid rgba(255,255,255,0.07)' }} data-testid="btx-operational-disclaimer">
            <p
              className="text-[11px] sm:text-xs leading-relaxed text-center"
              style={{ color: colors.white, opacity: 0.5 }}
            >
              BTX and Black Tie Holdings independently operate the BTX marketplace environment, including onboarding, KYC verification, settlement processes, and transaction activity associated with marketplace participation. AZReserves provides reserve infrastructure and related reserve-linked asset framework components only.
            </p>
          </div>

          {/* Bottom CTA */}
          <div className="mt-8 flex justify-center">
            <a
              href={BTX_SIGNUP_URL}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="access-explore-marketplace-btn"
              className="inline-flex items-center gap-2 px-7 py-3.5 rounded-lg text-sm font-semibold uppercase tracking-[0.14em] transition-all duration-300"
              style={{
                backgroundColor: 'transparent',
                color: colors.teal,
                border: `1px solid ${colors.teal}`,
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.backgroundColor = `${colors.teal}14`;
                e.currentTarget.style.boxShadow = `0 0 0 3px ${colors.teal}1f`;
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor = 'transparent';
                e.currentTarget.style.boxShadow = 'none';
              }}
            >
              Explore the Marketplace
              <ArrowRight className="w-4 h-4" />
            </a>
          </div>
          </div>
        </div>
      </section>

      {/* ==========  COMPARISON TABLE  ========== */}
      <section
        id="why-azg"
        className="py-20 sm:py-28"
        style={{
          background:
            'linear-gradient(180deg, #050505 0%, #0A0A0A 50%, #050505 100%)',
        }}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <SectionHeading
            title="WHY AZG IS DIFFERENT"
            sub="Built around reserve infrastructure, not simply asset representation."
          />

          <p
            className="text-xs sm:text-sm leading-relaxed text-center max-w-2xl mx-auto -mt-10 mb-14"
            style={{ color: colors.white, opacity: 0.45 }}
          >
            Framework principles also apply across the broader Arizore reserve infrastructure ecosystem.
          </p>

          {/* Comparison table — 2 columns */}
          <div
            className="rounded-2xl overflow-x-auto"
            style={{
              border: `1px solid rgba(255,255,255,0.06)`,
              background:
                'linear-gradient(180deg, rgba(255,255,255,0.025) 0%, rgba(255,255,255,0.01) 100%)',
              boxShadow:
                '0 30px 60px -30px rgba(0,0,0,0.7), 0 0 0 1px rgba(0,128,128,0.04), inset 0 1px 0 rgba(255,255,255,0.04)',
            }}
            data-testid="azg-comparison-table"
          >
            <table className="w-full text-left min-w-[680px]" style={{ borderCollapse: 'collapse' }}>
              <thead>
                <tr style={{ borderBottom: `1px solid ${colors.teal}20` }}>
                  <th
                    className="py-5 px-5 sm:px-6 text-xs uppercase tracking-[0.16em] font-semibold align-bottom"
                    style={{ color: colors.teal, width: '28%' }}
                  >
                    Features That Matter
                  </th>
                  <th
                    className="py-5 px-5 sm:px-6 text-xs uppercase tracking-[0.16em] font-semibold align-bottom"
                    style={{ color: colors.white, opacity: 0.7, width: '36%' }}
                  >
                    Traditional Gold-Backed Models
                  </th>
                  <th
                    className="py-5 px-5 sm:px-6 text-xs uppercase tracking-[0.16em] font-semibold align-bottom relative"
                    style={{
                      color: colors.white,
                      width: '36%',
                      backgroundColor: `${colors.teal}10`,
                      borderLeft: `1px solid ${colors.teal}50`,
                    }}
                  >
                    AZG Framework
                  </th>
                </tr>
              </thead>
              <tbody>
                {comparisonRows.map((row, idx) => {
                  const RowIcon = row.icon;
                  return (
                    <tr
                      key={row.feature}
                      style={{
                        borderBottom:
                          idx === comparisonRows.length - 1
                            ? 'none'
                            : `1px solid rgba(255,255,255,0.05)`,
                      }}
                    >
                      {/* Feature column */}
                      <td className="py-6 px-5 sm:px-6 align-top">
                        <div className="flex items-start gap-3">
                          <RowIcon
                            className="w-5 h-5 mt-0.5 flex-shrink-0"
                            style={{ color: colors.teal }}
                            strokeWidth={1.5}
                          />
                          <div>
                            <div
                              className="text-sm font-semibold uppercase tracking-[0.12em] leading-snug"
                              style={{ color: colors.white }}
                            >
                              {row.feature}
                            </div>
                            <div
                              className="text-xs mt-1 leading-snug"
                              style={{ color: colors.white, opacity: 0.5 }}
                            >
                              {row.sub}
                            </div>
                          </div>
                        </div>
                      </td>

                      {/* Traditional column */}
                      <td className="py-6 px-5 sm:px-6 align-top">
                        <p
                          className="text-sm leading-relaxed"
                          style={{ color: colors.white, opacity: 0.55 }}
                        >
                          {row.traditional}
                        </p>
                      </td>

                      {/* AZG column (highlighted) */}
                      <td
                        className="py-6 px-5 sm:px-6 align-top"
                        style={{
                          backgroundColor: `${colors.teal}08`,
                          borderLeft: `1px solid ${colors.teal}40`,
                        }}
                      >
                        <p
                          className="text-sm leading-relaxed"
                          style={{ color: colors.white, opacity: 0.85 }}
                        >
                          {row.azg}
                        </p>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          <p
            className="text-xs mt-6 max-w-2xl mx-auto text-center"
            style={{ color: colors.white, opacity: 0.4 }}
          >
            Comparison reflects general characteristics of traditional gold-backed token models relative to the AZG reserve framework. Not a comparison to any specific named product or issuer.
          </p>
        </div>
      </section>

      {/* ==========  RESERVE ROOM — INSTITUTIONAL MEDIA  ========== */}
      <section
        className="py-20 sm:py-28 relative overflow-hidden"
        style={{
          background:
            'linear-gradient(180deg, #050505 0%, #0A0A0A 55%, #050505 100%)',
        }}
        aria-label="Reserve Room"
      >
        {/* Ambient teal lift */}
        <div
          aria-hidden="true"
          className="absolute inset-0 pointer-events-none"
          style={{
            background:
              'radial-gradient(50% 50% at 50% 0%, rgba(0,128,128,0.08) 0%, rgba(0,0,0,0) 70%)',
          }}
        />

        <div className="relative max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Eyebrow */}
          <div className="flex items-center gap-3 mb-5 justify-center">
            <span className="h-px w-8" style={{ backgroundColor: colors.teal }} />
            <span
              className="inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.28em] font-medium"
              style={{ color: colors.teal }}
            >
              <Mic className="w-3.5 h-3.5" strokeWidth={1.5} />
              Reserve Room
            </span>
            <span className="h-px w-8" style={{ backgroundColor: colors.teal }} />
          </div>

          {/* Headline */}
          <h2
            className="text-center text-3xl sm:text-4xl md:text-5xl font-bold mb-5 tracking-tight"
            style={{ color: colors.white }}
          >
            REAL ASSETS. REAL INFRASTRUCTURE.
          </h2>
          <p
            className="text-center text-base sm:text-lg leading-relaxed max-w-2xl mx-auto mb-12 sm:mb-14"
            style={{ color: colors.white, opacity: 0.65 }}
          >
            Exploring reserve-backed digital asset infrastructure and the transition from speculative tokenisation toward structured financial systems.
          </p>

          {/* Editorial layout: artwork + meta */}
          <div className="grid lg:grid-cols-12 gap-8 lg:gap-10 items-center">
            {/* Artwork */}
            <a
              href="https://open.spotify.com/episode/40I8kdg7qb55RTIcAr0tWV?si=0b605e50a15e441d"
              target="_blank"
              rel="noopener noreferrer"
              data-testid="reserve-room-artwork-link"
              className="group relative block lg:col-span-7 rounded-2xl overflow-hidden"
              style={{
                border: '1px solid rgba(255,255,255,0.08)',
                boxShadow:
                  '0 30px 60px -30px rgba(0,0,0,0.7), inset 0 1px 0 rgba(255,255,255,0.04)',
              }}
            >
              <picture>
                <source srcSet="/reserve-room-ep2-thumbnail.webp" type="image/webp" />
                <img
                  src="/reserve-room-ep2-thumbnail.jpg"
                  alt="The Reserve Room — Episode 2: Real Assets, Real Infrastructure"
                  data-testid="reserve-room-thumbnail"
                  className="block w-full h-auto transition-transform duration-[1200ms] ease-out group-hover:scale-[1.02]"
                  loading="lazy"
                  decoding="async"
                />
              </picture>

              {/* Subtle overlay gradient */}
              <div
                aria-hidden="true"
                className="absolute inset-0 pointer-events-none transition-opacity duration-500"
                style={{
                  background:
                    'linear-gradient(180deg, rgba(0,0,0,0) 55%, rgba(0,0,0,0.55) 100%)',
                }}
              />

              {/* Elegant play icon */}
              <div
                aria-hidden="true"
                className="absolute inset-0 flex items-center justify-center pointer-events-none"
              >
                <div
                  className="flex items-center justify-center rounded-full transition-all duration-500 group-hover:scale-110"
                  style={{
                    width: 76,
                    height: 76,
                    backgroundColor: 'rgba(0,0,0,0.55)',
                    border: '1px solid rgba(255,255,255,0.4)',
                    backdropFilter: 'blur(8px)',
                    WebkitBackdropFilter: 'blur(8px)',
                    boxShadow:
                      '0 10px 30px -10px rgba(0,0,0,0.6), inset 0 1px 0 rgba(255,255,255,0.08)',
                  }}
                >
                  <Play
                    className="w-7 h-7"
                    style={{ color: colors.white, marginLeft: 3 }}
                    strokeWidth={1.5}
                    fill={colors.white}
                  />
                </div>
              </div>
            </a>

            {/* Meta + CTA */}
            <div className="lg:col-span-5">
              <span
                className="block text-[10px] uppercase tracking-[0.32em] font-medium mb-4"
                style={{ color: colors.teal, opacity: 0.85 }}
              >
                Episode 02 · The Reserve Room
              </span>
              <h3
                className="text-2xl sm:text-3xl font-semibold leading-snug tracking-tight mb-4"
                style={{ color: colors.white }}
              >
                From Crypto Hype to Financial Infrastructure
              </h3>
              <p
                className="text-sm sm:text-base leading-relaxed mb-3"
                style={{ color: colors.white, opacity: 0.65 }}
              >
                George Boot × Caroline Macdonald examine the structural shift away from speculative tokenisation toward reserve-backed digital asset infrastructure.
              </p>
              <p
                className="text-xs uppercase tracking-[0.22em] font-medium mb-8"
                style={{ color: colors.white, opacity: 0.4 }}
              >
                Powered by Arizore · Black Tie Holdings
              </p>

              <a
                href="https://open.spotify.com/episode/40I8kdg7qb55RTIcAr0tWV?si=0b605e50a15e441d"
                target="_blank"
                rel="noopener noreferrer"
                data-testid="reserve-room-listen-spotify-btn"
                className="inline-flex items-center gap-2 px-7 py-3.5 rounded-lg text-sm font-semibold uppercase tracking-[0.16em] transition-all duration-300"
                style={{
                  backgroundColor: colors.teal,
                  color: colors.white,
                  border: `1px solid ${colors.teal}`,
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.boxShadow = `0 0 0 4px ${colors.teal}26`;
                  e.currentTarget.style.transform = 'translateY(-1px)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.boxShadow = 'none';
                  e.currentTarget.style.transform = 'translateY(0)';
                }}
              >
                Listen on Spotify
                <ArrowUpRight className="w-4 h-4" />
              </a>
            </div>
          </div>
        </div>
      </section>

    </div>
  );
};

export default AZGLandingPage;
